import { useState, type ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { ParamType, type ParamValue } from '@gr/core/platform/config/registry';
import type { PersonalSettingDto } from '@gr/core/platform/user-preferences/dto';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { BareInput } from '../../../../ui-kit/Field/Field';
import { inputText, parseSettingInput, unitOf } from '../../../../shared/config/setting-input';
import { docRefLines, splitDocRefs } from '../../../../shared/config/doc-refs';
import { InfoTip } from '../../../../ui-kit/InfoTip/InfoTip';
import styles from './PersonalSettings.module.scss';

/** Чем закончилось сохранение или возврат к значению платформы. */
export type PersonalOutcome = { ok: true } | { ok: false; errors: string[] };

export interface PersonalSettingsProps {
  settings: readonly PersonalSettingDto[];
  onSave(key: string, value: ParamValue): Promise<PersonalOutcome>;
  /** Вернуться к значению платформы. */
  onReset(key: string): Promise<PersonalOutcome>;
}

/** PREF-UC-3: личные настройки отображения — своё значение рядом со значением платформы. */
export function PersonalSettings({ settings, onSave, onReset }: PersonalSettingsProps): ReactElement {
  const { t } = useT();
  if (settings.length === 0) return <EmptyState title={t('preferences.personal.empty')} />;
  return (
    <ul className={styles.list}>
      {settings.map((setting) => (
        <PersonalRow key={setting.key} setting={setting} onSave={onSave} onReset={onReset} />
      ))}
    </ul>
  );
}

function PersonalRow({ setting, onSave, onReset }: { setting: PersonalSettingDto; onSave: PersonalSettingsProps['onSave']; onReset: PersonalSettingsProps['onReset'] }): ReactElement {
  const { t, te, locale } = useT();
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);
  const unit = unitOf(setting.key);
  // CFG-RULE-2: коды документации — в подсказке «i» после подписи, а не в самой подписи.
  const description = splitDocRefs(setting.description[locale]);
  const label = description.text;

  const text = (value: ParamValue | null): string => {
    const plain = inputText(value);
    return unit ? `${plain} ${t(`config.units.${unit}`)}` : plain;
  };

  const run = async (action: () => Promise<PersonalOutcome>): Promise<void> => {
    setBusy(true);
    const result = await action();
    setBusy(false);
    setErrors(result.ok ? null : result.errors);
    if (result.ok) setDraft(null);
  };

  const save = (): void => {
    if (draft === null) return;
    const value = parseSettingInput(setting, draft);
    if (value === null) {
      setErrors([ErrorCode.ConfigValueInvalid]);
      return;
    }
    void run(() => onSave(setting.key, value));
  };

  const own = setting.personalValue !== null && !setting.personalInvalid;
  const numeric = setting.type === ParamType.Integer || setting.type === ParamType.Number;
  const range = setting.min !== null ? t('config.range', { min: String(setting.min), max: String(setting.max ?? '—') }) : null;

  return (
    <li className={styles.row} data-setting={setting.key}>
      <div className={styles.head}>
        <span className={styles.title}>
          {label}
          {description.refs.length > 0 ? (
            <>
              {' '}
              <InfoTip label={t('config.docRefs.label')} heading={t('config.docRefs.heading')} lines={docRefLines(description.refs, t)} />
            </>
          ) : null}
        </span>
        {own ? <Badge tone="accent">{t('preferences.personal.own')}</Badge> : null}
      </div>

      {draft !== null ? (
        <div className={styles.editor}>
          <BareInput label={label} value={draft} disabled={busy} inputMode={numeric ? 'decimal' : undefined} autoFocus onChange={(e) => setDraft(e.target.value)} />
          {range ? <p className="u-muted u-small">{range}</p> : null}
        </div>
      ) : (
        <p className={styles.value}>
          <strong>{text(setting.value)}</strong>
          {own ? <span className="u-muted u-small">{t('preferences.personal.platform', { value: text(setting.platformValue) })}</span> : null}
        </p>
      )}
      {setting.personalInvalid ? <p className="u-text-danger u-small">{t('preferences.personal.invalid', { value: text(setting.personalValue) })}</p> : null}

      {errors ? (
        <Alert tone="danger" title={t('preferences.personal.notSaved')}>
          {te(errors)}
        </Alert>
      ) : null}

      <div className={styles.actions}>
        {draft !== null ? (
          <>
            <Button variant="primary" size="sm" icon="check" loading={busy} onClick={save}>
              {t('common.save')}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              icon="edit"
              onClick={() => {
                setErrors(null);
                setDraft(inputText(setting.value));
              }}
            >
              {t('preferences.personal.edit')}
            </Button>
            {setting.personalValue !== null ? (
              <Button size="sm" variant="ghost" loading={busy} onClick={() => void run(() => onReset(setting.key))}>
                {t('preferences.personal.reset')}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}
